import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Settings, Users, Package, BarChart2, LayoutDashboard, Folder, Clock, History } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom'; // Importar Link

const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  if (!isAdmin) {
    // Redirigir si no es administrador
    navigate('/');
    return null;
  }

  const adminCards = [
    {
      title: t('product.title'),
      description: 'Administra el inventario y los detalles de los productos.',
      icon: Package,
      color: 'from-blue-500 to-blue-600',
      link: '/admin/products' 
    },
    {
      title: t('category.title'),
      description: 'Organiza y edita las categorías de productos.',
      icon: Folder,
      color: 'from-teal-500 to-teal-600',
      link: '/admin/categories' 
    },
    {
      title: t('user.title'),
      description: 'Controla los accesos y roles de los usuarios.',
      icon: Users,
      color: 'from-green-500 to-green-600',
      link: '/admin/users' 
    },
    {
      title: t('statistics.title'),
      description: 'Visualiza el rendimiento y las métricas clave.',
      icon: BarChart2,
      color: 'from-purple-500 to-purple-600',
      link: '/admin/sales' 
    },
    {
      title: t('history.title'),
      description: 'Rastrea todos los cambios realizados en el sistema.',
      icon: History,
      color: 'from-pink-500 to-pink-600',
      link: '/admin/edit-history' 
    },
    {
      title: t('history.access'),
      description: 'Monitorea los inicios de sesión y la actividad de los usuarios.',
      icon: Clock,
      color: 'from-indigo-500 to-indigo-600',
      link: '/admin/access-history' 
    },
    {
      title: t('settings.title'),
      description: 'Ajusta las opciones generales del portal, logo, redes y slider.',
      icon: Settings,
      color: 'from-orange-500 to-orange-600',
      link: '/admin/settings'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4 flex items-center gap-3">
            <LayoutDashboard className="w-10 h-10 text-blue-600" />
            {t('nav.dashboard')}
          </h2>
          <p className="text-xl text-gray-600">
            Bienvenido, administrador. Aquí puedes gestionar todos los aspectos de tu portal B2B.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminCards.map((card, index) => (
            <Link to={card.link} key={index}> {/* Envolver en Link */}
              <motion.div
                className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-white/20 cursor-pointer hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br ${card.color}`}>
                  <card.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-gray-600 text-sm">{card.description}</p>
                <motion.button
                  className="mt-6 w-full bg-gray-100 text-gray-800 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  // onClick={() => alert(`Navegando a: ${card.title}`)} // Simulación de navegación
                >
                  Ir a {card.title.split(' ')[0]}
                </motion.button>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;